import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Card, Container, Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { post, get, patch } from '../../../../helpers/httpClient';
import { toast } from 'react-toastify';
import ImageUploader from '../../../../helpers/ImageUploader';
import PageBasicFields from '../../../../helpers/PageBasicFields';

function normalizeStructure(struct) {
  const base = struct && typeof struct === 'object' ? struct : {};
  const cols = Array.isArray(base.columns) ? base.columns : [];
  const normalizedCols = cols.map((col) => ({
    properties: Array.isArray(col?.properties)
      ? col.properties.map((p) => {
          const type = ['image', 'text', 'line'].includes((p?.type || '').toLowerCase()) ? (p.type || '').toLowerCase() : 'text';
          let meta = null;
          if (type === 'line') {
            const m = (p && p.meta && typeof p.meta === 'object') ? p.meta : {};
            const fs = String(m.fontSize ?? '').toLowerCase();
            const allowedFontSizes = ['heading', 'sub-heading', 'text'];
            meta = {
              prefix: String(m.prefix ?? ''),
              suffix: String(m.suffix ?? ''),
              fontSize: allowedFontSizes.includes(fs) ? fs : 'text',
            };
          }
          return {
            name: String(p?.name ?? ''),
            type,
            required: !!p?.required,
            show_name: p?.show_name === undefined ? true : !!p.show_name,
            meta,
          };
        })
      : [],
  }));
  return { columns: normalizedCols };
}

function GalleryItemEditor() {
  const navigate = useNavigate();
  const { id, pageId } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = !!pageId;

  const [form, setForm] = useState({ slug: '', title: '', preview: null, extra: { props: {} } });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [parentPage, setParentPage] = useState(null);
  const [structure, setStructure] = useState({ columns: [] });

  const titleText = useMemo(() => (isEdit ? 'Edit Gallery item' : 'New Gallery item'), [isEdit]);

  // Load current item (when editing) and determine parent, then load parent gallery to get structure
  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setLoading(true);
        setError('');
        if (isEdit) {
          const itemResp = await get(`pages/${pageId}`);
          const item = itemResp?.page || itemResp;
          if (!item) throw new Error('Item not found');
          if (!mounted) return;
          setForm({ slug: item.slug || '', title: item.title || '', preview: item.preview || null, extra: item.extra || { props: {} } });
          // try to load parent by id
          if (item.parent) {
            try {
              const parentResp = await get(`pages/${item.parent.id}`);
              const parent = parentResp?.page || parentResp;
              if (mounted) {
                setParentPage(parent);
                setStructure(normalizeStructure(parent?.extra || {}));
              }
            } catch (e) {
              // fallback: load all pages and find by id
              const list = await get(`websites/${id}/pages`);
              const pages = Array.isArray(list) ? list : (list?.pages || []);
              const parent = pages.find(p => p.id === item.parent);
              if (mounted) {
                setParentPage(parent || null);
                setStructure(normalizeStructure(parent?.extra || {}));
              }
            }
          }
        } else {
          // creating new: load parent by slug from query
          const parentId = searchParams.get('parent');
          if (parentId) {
            const resp = await get(`pages/${parentId}`)
            const parent = resp.page;
            if (!parent) throw new Error('Parent gallery not found');
            if (String((resp.type || '')).toLowerCase() !== 'gallery') {
              toast.warn('Selected parent is not a Gallery.');
            }
            if (mounted) {
              setParentPage(parent);
              setStructure(normalizeStructure(parent?.extra || {}));
            }
          } else {
            toast.warn('No parent selected. Please navigate from a gallery or provide a parent parameter.');
          }
        }
      } catch (e) {
        if (mounted) setError('Failed to load data.');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [id, pageId, isEdit, searchParams]);

  const handleChangeProp = (name, value) => {
    setForm(prev => ({ ...prev, extra: { ...(prev.extra || {}), props: { ...(prev.extra?.props || {}), [name]: value } } }));
  };

  const validateRequired = () => {
    const props = form?.extra?.props || {};
    const missing = [];
    for (const col of structure.columns || []) {
      for (const p of col.properties || []) {
        if (p.required) {
          const v = props[p.name];
          if (p.type === 'image') {
            if (!v || typeof v !== 'string' || v.trim() === '') missing.push(p.name);
          } else {
            if (v === undefined || v === null || String(v).trim() === '') missing.push(p.name);
          }
        }
      }
    }
    return missing;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.title.trim()) {
      toast.error('Title is required.');
      return;
    }
    const missing = validateRequired();
    if (missing.length > 0) {
      toast.error(`Please fill required fields: ${missing.join(', ')}`);
      return;
    }

    const payload = {
      type: 'gallery-item',
      slug: (form.slug || '').trim(),
      title: (form.title || '').trim(),
      preview: form.preview || null,
      components: form.components || [],
      extra: form.extra || { props: {} },
    };

    try {
      setSaving(true);
      setError('');
      if (isEdit) {
        await patch(`pages/${pageId}`, payload);
      } else {
        const parent = searchParams.get('parent');
        await post(`websites/${id}/pages`, { ...payload, order: 0, parent: parent || null });
      }
      navigate(`/websites/${id}?tab=structure`);
    } catch (err) {
      toast.error(isEdit ? 'Failed to update page.' : 'Failed to create page.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => navigate(-1);

  const propsMap = form?.extra?.props || {};

  const renderPropertyInput = (p) => {
    const label = p.show_name !== false ? p.name : '';
    const key = p.name;
    if (!key) return null;
    switch (p.type) {
      case 'image':
        return (
          <div key={key} className="mb-2">
            {label && <Form.Label>{label}</Form.Label>}
            <ImageUploader websiteId={id} onUploaded={(resp) => { handleChangeProp(key, resp.url); }} />
            {propsMap[key] && (
              <img src={propsMap[key]} alt={key} style={{ maxHeight: '4rem', marginTop: 8 }} />
            )}
          </div>
        );
      case 'line':
      case 'text':
      default:
        return (
          <div key={key} className="mb-2">
            {label && <Form.Label>{label}</Form.Label>}
            <Form.Control
              type="text"
              value={propsMap[key] || ''}
              onChange={(e) => handleChangeProp(key, e.target.value)}
              placeholder={p.type === 'line' ? `${p.meta?.prefix || ''}${p.show_name ? ` ${p.name}` : ''}${p.meta?.suffix || ''}`.trim() : 'Enter value'}
            />
          </div>
        );
    }
  };

  return (
    <Container className="mt-4">
      <Row>
        <Col md={{ span: 10, offset: 0 }} lg={{ span: 9, offset: 0 }}>
          <Card>
            <Card.Body>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <h2 style={{ margin: 0 }}>{titleText}</h2>
              </div>

              {error && <Alert variant="danger">{error}</Alert>}

              {loading ? (
                <div className="text-center my-3"><Spinner animation="border" /></div>
              ) : (
                <Form onSubmit={handleSubmit}>
                  {!parentPage && (
                    <Alert variant="warning">Parent gallery is not set. Please go back and add an item under a gallery.</Alert>
                  )}

                  <PageBasicFields
                    form={form}
                    setForm={setForm}
                    websiteId={id}
                    slugPlaceholder="e.g. product-123"
                    titlePlaceholder="Enter title"
                    preserveEmptySlug={isEdit}
                  />

                  <div className="form-row-horizontal mb-2">
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, width: '100%' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <strong>Item properties</strong>
                        {parentPage && <span style={{ color: '#64748b' }}>From gallery: {parentPage.title || parentPage.slug}</span>}
                      </div>
                      {structure.columns.length === 0 && (
                        <div style={{ color: '#64748b', fontSize: 14 }}>Parent gallery has no item structure defined.</div>
                      )}
                      {structure.columns.map((col, idx) => (
                        <div key={idx} style={{ border: '1px dashed #cbd5e1', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                          <div style={{ fontWeight: 600, marginBottom: 8 }}>Column #{idx + 1}</div>
                          {col.properties.length === 0 && (
                            <div style={{ color: '#94a3b8', fontSize: 13 }}>No properties</div>
                          )}
                          {col.properties.map((p) => renderPropertyInput(p))}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button type="submit" className="primary-button" disabled={saving}>
                      {saving ? (<><Spinner animation="border" size="sm" className="me-2" /> Saving...</>) : 'Save'}
                    </Button>
                    <Button className="primary-button" onClick={handleCancel} disabled={saving}>Cancel</Button>
                  </div>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default GalleryItemEditor;
